import { ArrowLeft, Edit, Loader2, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { QrCodeDisplay } from '@/components/ui/qr-code-display';
import { Skeleton } from '@/components/ui/skeleton';
import { useEquipmentTypes } from '@/features/equipment-types/queries';
import {
  useDeleteEquipment,
  useEquipment,
  useEquipmentItem,
} from '@/features/equipment/queries';
import {
  criticalityColor,
  type Equipment,
} from '@/features/equipment/types';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { CountersCard } from './CountersCard';
import { EquipmentWarrantyCard } from './EquipmentWarrantyCard';

function Field({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) {
  const display =
    value === null || value === undefined || value === '' ? '—' : value;
  return (
    <div className={cn(fullWidth && 'sm:col-span-2')}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{display}</dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">{children}</dl>
      </CardContent>
    </Card>
  );
}

/** Format a YYYY-MM-DD or full ISO string into the user's locale. */
function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

function DetailContent({ eq }: { eq: Equipment }) {
  const { t } = useTranslation();
  const { data: types } = useEquipmentTypes();
  const { data: vessels } = useVessels();
  // Children: load vessel-scope and filter to those whose parent is `eq`.
  // Parent: same vessel-scope list, find by id.
  const { data: vesselEquipment } = useEquipment({ vesselId: eq.vesselId });

  const typeName = useMemo(
    () => types?.find((x) => x.id === eq.equipmentTypeId)?.name ?? null,
    [types, eq.equipmentTypeId],
  );
  const vesselName = useMemo(() => {
    const v = vessels?.find((x) => x.id === eq.vesselId);
    return v ? vesselDisplayName(v) : null;
  }, [vessels, eq.vesselId]);

  const parent = useMemo(() => {
    if (!eq.parentEquipmentId || !vesselEquipment) return null;
    return vesselEquipment.find((x) => x.id === eq.parentEquipmentId) ?? null;
  }, [vesselEquipment, eq.parentEquipmentId]);

  const children = useMemo(
    () => (vesselEquipment ?? []).filter((x) => x.parentEquipmentId === eq.id),
    [vesselEquipment, eq.id],
  );

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={t('equipment.detail.sectionIdentity')}>
        <Field
          label={t('equipment.fields.tag')}
          value={<span className="font-mono">{eq.tag}</span>}
        />
        <Field label={t('equipment.fields.name')} value={eq.name} />
        <Field
          label={t('equipment.fields.serialNumber')}
          value={eq.serialNumber}
          fullWidth
        />
      </Section>

      <Section title={t('equipment.detail.sectionTypeLocation')}>
        <Field label={t('equipment.fields.equipmentTypeId')} value={typeName} />
        <Field label={t('equipment.fields.vesselId')} value={vesselName} />
        <Field
          label={t('equipment.fields.location')}
          value={eq.location}
          fullWidth
        />
      </Section>

      <Section title={t('equipment.detail.sectionHierarchy')}>
        <Field
          label={t('equipment.fields.parentEquipmentId')}
          value={
            parent ? (
              <Link
                to={`/equipment/${parent.id}`}
                className="text-primary hover:underline"
              >
                <span className="font-mono">{parent.tag}</span> — {parent.name}
              </Link>
            ) : null
          }
          fullWidth
        />
        <Field
          label={t('equipment.detail.sectionHierarchy')}
          value={
            children.length === 0 ? null : (
              <ul className="space-y-1">
                {children.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/equipment/${c.id}`}
                      className="text-primary hover:underline"
                    >
                      <span className="font-mono">{c.tag}</span> — {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )
          }
          fullWidth
        />
      </Section>

      <Section title={t('equipment.detail.sectionCommissioning')}>
        <Field
          label={t('equipment.fields.installedAt')}
          value={formatDate(eq.installedAt)}
        />
        <Field
          label={t('equipment.fields.commissionedAt')}
          value={formatDate(eq.commissionedAt)}
        />
        <Field
          label={t('equipment.fields.warrantyEndDate')}
          value={formatDate(eq.warrantyEndDate)}
        />
      </Section>

      <Section title={t('equipment.detail.sectionOperational')}>
        <Field
          label={t('equipment.fields.criticality')}
          value={
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                criticalityColor(eq.criticality),
              )}
            >
              {t(`equipment.criticality.${eq.criticality}`)}
            </span>
          }
        />
        <Field
          label={t('equipment.fields.isActive')}
          value={
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                eq.isActive
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {t(`equipment.status.${eq.isActive ? 'active' : 'inactive'}`)}
            </span>
          }
        />
      </Section>
    </div>
  );
}

export function EquipmentDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: eq, isLoading, isError, error, refetch } = useEquipmentItem(id);
  const deleteMut = useDeleteEquipment();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('equipment.detail.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('equipment.detail.loadError');
  })();

  const onConfirmDelete = () => {
    if (!id) return;
    deleteMut.mutate(id, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/equipment');
      },
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
            <Link to="/equipment">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">
            {eq ? eq.name : t('common.loading')}
          </h1>
          {eq && (
            <span className="font-mono text-sm text-muted-foreground">
              ({eq.tag})
            </span>
          )}
        </div>
        {eq && (
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to={`/equipment/${eq.id}/edit`}>
                <Edit aria-hidden />
                {t('common.edit')}
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Trash2 aria-hidden />
              )}
              {t('common.delete')}
            </Button>
          </div>
        )}
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('equipment.detail.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}
      {eq && (
        <>
          <DetailContent eq={eq} />
          <EquipmentWarrantyCard equipment={eq} />
          <CountersCard equipment={eq} />
        </>
      )}
      {eq && <QrCodeDisplay entityType="equipment" entityId={eq.id} />}

      {eq && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t('equipment.delete.title')}
          description={t('equipment.delete.description', { name: eq.name })}
          confirmLabel={t('equipment.delete.confirm')}
          variant="destructive"
          loading={deleteMut.isPending}
          onConfirm={onConfirmDelete}
        />
      )}
    </div>
  );
}
